import { Button, Chat } from '@servicecompressionllc/sc-design-system';
import { Lock } from '@servicecompressionllc/sc-design-system/icons';
import { formatDayTime } from '../../../lib/format.js';
import { displayName, initialsOf } from './PeoplePicker.js';
import { useSharedConversationLogic } from './useSharedWithMeLogic.js';

const firstName = (name: string): string => name.split(' ')[0] ?? name;

/** Someone else's conversation as they shared it: read-only, with Continue in my chat. */
export const SharedConversationView = ({ shareId }: { shareId: string }) => {
  const { read, continueInMyChat } = useSharedConversationLogic(shareId);

  if (read.isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center text-sm text-ink-muted">
        Loading conversation…
      </div>
    );
  }
  if (!read.data) {
    return (
      <div className="flex flex-1 items-center justify-center p-8 text-center text-sm text-ink-muted">
        This conversation isn’t shared with you anymore.
      </div>
    );
  }

  const { share, messages } = read.data;
  const sharer = displayName(share.sharedBy);
  return (
    <div className="flex min-h-0 min-w-0 flex-1 flex-col">
      <div className="flex items-center gap-3 border-b border-outline px-5 py-2.5">
        <div className="flex min-w-0 flex-col gap-0.5">
          <span className="truncate text-sm font-semibold text-ink">{share.title}</span>
          <span className="text-xs text-ink-subtle">
            Shared by {sharer} · {formatDayTime(share.sharedAt)} · read-only
          </span>
        </div>
        <div className="flex-1" />
        <Button
          size="sm"
          onClick={() => continueInMyChat.mutate()}
          disabled={continueInMyChat.isPending}
        >
          Continue in my chat
        </Button>
      </div>

      <Chat aria-label={`Conversation shared by ${sharer}`}>
        <Chat.MessageList>
          {share.note && (
            <div className="flex items-start gap-2.5 rounded border border-outline bg-surface-elevated px-3.5 py-2.5">
              <span
                aria-hidden
                className="grid h-6 w-6 flex-none place-items-center rounded-full bg-surface text-[10px] font-semibold text-ink"
              >
                {initialsOf(share.sharedBy)}
              </span>
              <div className="flex flex-col gap-0.5 text-sm leading-normal">
                <span className="text-xs text-ink-subtle">Note from {firstName(sharer)}</span>
                <span className="text-ink">{share.note}</span>
              </div>
            </div>
          )}
          {messages.map((m) =>
            m.role === 'assistant' ? (
              <Chat.Message key={m.id} role="assistant">
                <Chat.MessageAvatar>SC</Chat.MessageAvatar>
                <div className="flex min-w-0 flex-col gap-1.5">
                  {m.toolActivity && <Chat.ToolActivity items={m.toolActivity} settled />}
                  <Chat.MessageBubble>
                    <Chat.MessageMarkdown>{m.content}</Chat.MessageMarkdown>
                  </Chat.MessageBubble>
                </div>
              </Chat.Message>
            ) : (
              <Chat.Message key={m.id} role={m.role}>
                <div className="flex flex-col items-end gap-1">
                  <span className="text-[11px] text-ink-subtle">{sharer}</span>
                  <Chat.MessageBubble>
                    <Chat.MessageContent>{m.content}</Chat.MessageContent>
                  </Chat.MessageBubble>
                </div>
              </Chat.Message>
            ),
          )}
        </Chat.MessageList>
      </Chat>

      <div className="flex items-center gap-3 border-t border-outline bg-surface px-5 py-3.5">
        <Lock size={16} aria-hidden className="flex-none text-ink-subtle" />
        <span className="text-[13px] text-ink-muted">
          This is {firstName(sharer)}’s conversation. To ask follow-ups, continue it as your own;
          your questions use your own access.
        </span>
        {continueInMyChat.error && (
          <span className="text-xs font-semibold text-danger">
            {continueInMyChat.error.message}
          </span>
        )}
      </div>
    </div>
  );
};
